// =====================================================================
// Certificação — o PLACAR da tela de relatórios.
//
// A tela já sabia listar; não sabia MEDIR. Medido em 03/09/2026, com 25
// relatórios na tela: 23 em aberto, e o fato que domina o programa —
// **21 esperam a assinatura de um gerente** — não aparecia em lugar nenhum.
// Estas funções extraem da mesma lista as quatro perguntas de quem toca o
// programa:
//
//   1. "Quantos estão em aberto?"                → resumirFila().emAberto
//   2. "Quantos esperam validação, e de quem?"   → .aguardandoValidacao / .faltaGerente
//   3. "Quantos na Etapa 1 e quantos na Etapa 2?"→ .etapa1 / .etapa2
//   4. "Como vai cada equipamento?"              → resumirPorEquipamento()
//
// ⚠️ QUEM SEGURA CADA RELATÓRIO SAI DE `aguardandoDe`, do certificacao-fila-utils.
// Não se reimplementa aqui: aquela função é a que já sabe que 'Em diagnóstico'
// com as duas assinaturas dadas NÃO espera validação — espera a Manutenção
// iniciar a Etapa 2. Contar por STATUS jogaria esse caso no balde errado.
//
// Puras de propósito (entram os relatórios, saem números): é a única lógica de
// risco do placar — o desenho não se testa.
// =====================================================================

import { aguardandoDe, diasParado } from './certificacao-fila-utils';

// Em que etapa do programa o relatório está. ⚠️ 'Rascunho' é Etapa 1 (é o
// autosave do diagnóstico) e 'Certificado com Ressalvas' é Etapa 2 (a
// certificação já foi feita, faltam as assinaturas). `null` = fora das etapas:
// terminou ou foi cancelado.
export const etapaDe = (status) => ({
  'Rascunho': 1,
  'Em diagnóstico': 1,
  'Em execução': 2,
  'Certificado com Ressalvas': 2,
}[status] || null);

/**
 * O placar da fila. Recebe os relatórios como a tela os tem e devolve as
 * contagens que ela precisa mostrar.
 *
 * ⚠️ `faltaGerente` e `faltaConsultor` SE SOBREPÕEM: o relatório em que os dois
 * ainda não assinaram conta nos dois. Somar um ao outro não dá
 * `aguardandoValidacao` — é por isso que a tela os escreve como "falta o gerente
 * em 21", nunca como duas fatias de uma barra.
 */
export const resumirFila = (lista, hoje = new Date()) => {
  const r = {
    total: 0,
    emAberto: 0,          // tudo que não é Certificado nem Cancelado
    certificados: 0,
    cancelados: 0,
    aguardandoValidacao: 0, // parado numa assinatura
    faltaGerente: 0,
    faltaConsultor: 0,
    comManutencao: 0,     // a bola está com quem executa, não com quem assina
    etapa1: 0,
    etapa2: 0,
    parados14: 0,         // em aberto há mais de 14 dias
    parados30: 0,         // em aberto há mais de 30 dias
    piorParado: null,     // maior tempo parado da fila (dias)
  };

  (lista || []).forEach((rel) => {
    r.total += 1;
    if (rel.STATUS === 'Certificado') { r.certificados += 1; return; }
    if (rel.STATUS === 'Cancelado') { r.cancelados += 1; return; }

    r.emAberto += 1;

    const etapa = etapaDe(rel.STATUS);
    if (etapa === 1) r.etapa1 += 1;
    if (etapa === 2) r.etapa2 += 1;

    const ag = aguardandoDe(rel);
    if (ag.tipo === 'validacao') {
      r.aguardandoValidacao += 1;
      if (ag.papeis.includes('Gerente')) r.faltaGerente += 1;
      if (ag.papeis.includes('Consultor')) r.faltaConsultor += 1;
    } else if (ag.tipo === 'trabalho') {
      r.comManutencao += 1;
    }

    const dias = diasParado(rel, hoje);
    if (dias !== null) {
      if (dias > 30) r.parados30 += 1;
      if (dias > 14) r.parados14 += 1;
      if (r.piorParado === null || dias > r.piorParado) r.piorParado = dias;
    }
  });

  return r;
};

/**
 * Como vai cada equipamento do programa.
 *
 * `parque` é o que vem do `GET /parque` do backend (o denominador: quantos
 * equipamentos daquele tipo existem na rede). `lojaFiltro` recorta esse
 * denominador — sem ele, filtrar a tela por ANV mostraria a cobertura da rede
 * ao lado dos relatórios de uma loja só.
 *
 * ⚠️ `ativosComRelatorio` conta ATIVOS DISTINTOS, não relatórios: dois
 * relatórios do mesmo equipamento (o do ano passado e o deste) são um
 * equipamento coberto, não dois. É essa a conta que fecha contra o parque.
 *
 * ⚠️⚠️ A chave é o **`ID_ATIVO`** (a FK para `dtbCertAtivo.CODIGO`, ex.:
 * `CHP-24JZ`), NUNCA o `NUMERO_ATIVO`. Os dois dão o mesmo número hoje (medido
 * em 03/09/2026: 13 e 10, idênticos), e é por isso que a troca é fácil de fazer
 * errado: `NUMERO_ATIVO` é o **snapshot** do patrimônio gravado no relatório, e
 * corrigir o patrimônio no cadastro do ativo passa a contar o MESMO equipamento
 * duas vezes na cobertura — sem erro em lugar nenhum. O patrimônio fica só como
 * reserva, para relatório antigo que não tenha a FK.
 *
 * ⚠️ `ativosComRelatorio` PODE passar do parque (equipamento baixado da loja
 * depois de certificado sai do denominador e não do numerador). A tela limita a
 * barra em 100% e mostra os dois números crus — inventar um teto silencioso
 * esconderia justamente o caso que merece olhar.
 */
export const resumirPorEquipamento = (lista, parque = [], lojaFiltro = '') => {
  const porNome = new Map();

  const garantir = (nome) => {
    if (!porNome.has(nome)) {
      porNome.set(nome, {
        nome, total: 0, etapa1: 0, etapa2: 0, certificados: 0,
        ativos: new Set(), parque: 0, versoesPublicadas: 0,
      });
    }
    return porNome.get(nome);
  };

  // O catálogo entra primeiro: equipamento com parque e sem nenhum relatório
  // precisa aparecer com zero, não desaparecer da leitura.
  (parque || []).forEach((eq) => {
    const item = garantir(eq.NOME);
    item.versoesPublicadas = Number(eq.VERSOES_PUBLICADAS) || 0;
    item.parque = lojaFiltro
      ? Number(eq.ATIVOS_POR_LOJA?.[lojaFiltro] || 0)
      : Number(eq.ATIVOS || 0);
  });

  (lista || []).forEach((rel) => {
    if (rel.STATUS === 'Cancelado') return; // cancelado não cobre nem pendencia
    const item = garantir(rel.EQUIPAMENTO || '—');
    item.total += 1;
    const etapa = etapaDe(rel.STATUS);
    if (etapa === 1) item.etapa1 += 1;
    if (etapa === 2) item.etapa2 += 1;
    if (rel.STATUS === 'Certificado') item.certificados += 1;
    const chaveAtivo = rel.ID_ATIVO || rel.NUMERO_ATIVO;
    if (chaveAtivo) item.ativos.add(chaveAtivo);
  });

  return [...porNome.values()]
    .map(({ ativos, ...resto }) => ({ ...resto, ativosComRelatorio: ativos.size }))
    // Quem tem trabalho primeiro; entre iguais, ordem alfabética estável.
    .sort((a, b) => b.total - a.total || b.parque - a.parque || a.nome.localeCompare(b.nome));
};

/**
 * Os tipos que estão no CATÁLOGO mas ainda não no programa: sem formulário
 * publicado e sem nenhum relatório. Medido em 03/09/2026 são 5 dos 7 — eles já
 * podem ser agendados no calendário, e é o agendamento que cobra a construção
 * do formulário. Dizer isso na tela evita a leitura de que o programa tem só
 * dois equipamentos.
 */
export const equipamentosForaDoPrograma = (porEquipamento = []) =>
  porEquipamento.filter((e) => e.versoesPublicadas === 0 && e.total === 0).map((e) => e.nome);

/** Só os que efetivamente estão rodando (têm formulário publicado ou relatório). */
export const equipamentosNoPrograma = (porEquipamento = []) =>
  porEquipamento.filter((e) => e.versoesPublicadas > 0 || e.total > 0);
